import React, { useState, useEffect } from "react";
import UserApiClient from "../../services/UserApiClient";

interface User {
  id: string;
  userName: string;
  firstName: string;
  lastName: string;
  role: string;
  email: string;
  isActive: boolean;
}

interface ResetPasswordProps {
  isOpen: boolean;
  user: User | null;
  businessId: string;
  onClose: () => void;
  onSave: () => void;
  isSelfReset?: boolean; // New prop to indicate if it's a self-reset
}

const ResetPassword: React.FC<ResetPasswordProps> = ({
  isOpen,
  user,
  businessId,
  onClose,
  onSave,
  isSelfReset = false,
}) => {
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [shouldShowPanel, setShouldShowPanel] = useState(false);

  // Handle slide animation
  useEffect(() => {
    if (isOpen) {
      // Add the open class on the next tick to trigger the transition
      const timer = setTimeout(() => {
        setShouldShowPanel(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setShouldShowPanel(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      // Reset form when opening
      setFormData({
        password: "",
        confirmPassword: "",
      });
      setError("");
      setSuccess("");
      setValidationErrors({});
    }
  }, [isOpen]);

  const validateForm = () => {
    const errors: { [key: string]: string } = {};

    // Password validation
    if (!formData.password.trim()) {
      errors.password = "Password is required";
    } else if (formData.password.length < 5) {
      errors.password = "Password must be at least 6 characters";
    }

    // Confirm password validation
    if (!formData.confirmPassword.trim()) {
      errors.confirmPassword = "Please confirm the password";
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Passwords do not match";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form before API call
    if (!validateForm()) {
      setError("Please fix the validation errors before submitting");
      return;
    }

    if (!user) {
      setError("No user selected for password reset");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      if (isSelfReset) {
        await UserApiClient.selfResetPassword(user.id, formData.password);
      } else {
        await UserApiClient.forceResetPassword(
          businessId,
          user.id,
          formData.password
        );
      }

      // Show success message
      setSuccess(
        isSelfReset
          ? "Password reset successfully!"
          : `Password reset successfully for ${user.firstName} ${user.lastName}!`
      );

      // Close the modal after a short delay to show the success message
      setTimeout(() => {
        onSave();
      }, 1500);
    } catch (error: any) {
      console.error("Error resetting password:", error);

      // Check if there's a specific error message from the API
      const errorMessage =
        error.response?.data?.Message ||
        error.response?.data?.message ||
        error.message ||
        "Failed to reset password";

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      password: "",
      confirmPassword: "",
    });
    setError("");
    setSuccess("");
    setValidationErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      {isOpen && <div className="slide-overlay" onClick={handleClose}></div>}

      {/* Slide Panel */}
      {isOpen && (
        <div
          className={`slide-panel ${shouldShowPanel ? "slide-panel-open" : ""}`}
        >
          <div className="slide-panel-header">
            <h2>{isSelfReset ? "Change Password" : "Reset Password"}</h2>
            <button className="slide-panel-close" onClick={handleClose}>
              ×
            </button>
          </div>

          <div className="slide-panel-content">
            {user && (
              <div
                style={{
                  background: "#f8f9fa",
                  padding: "1rem 1rem",
                  borderRadius: "8px",
                  marginBottom: "1rem",
                  border: "1px solid #e9ecef",
                }}
              >
                <h3 style={{ margin: "0 0 0.5rem 0", color: "#495057" }}>
                  {isSelfReset
                    ? "Changing password for your account:"
                    : "Resetting password for:"}
                </h3>
                <p style={{ margin: "0", fontWeight: "600", color: "#212529" }}>
                  Name: {user.firstName} {user.lastName} (UserId:{" "}
                  {user.userName})
                </p>
              </div>
            )}

            {success && (
              <div className="success-message" style={{ margin: "0 0 1rem 0" }}>
                ✅ {success}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="settings-grid">
                <div className="setting-item">
                  <label htmlFor="password">New Password *</label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`form-input ${
                      validationErrors.password ? "form-input-error" : ""
                    }`}
                    required
                    disabled={loading}
                    placeholder="Enter new password"
                    minLength={5}
                  />
                  {validationErrors.password && (
                    <span className="form-error">
                      {validationErrors.password}
                    </span>
                  )}
                </div>

                <div className="setting-item">
                  <label htmlFor="confirmPassword">Confirm Password *</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`form-input ${
                      validationErrors.confirmPassword ? "form-input-error" : ""
                    }`}
                    required
                    disabled={loading}
                    placeholder="Confirm new password"
                  />
                  {validationErrors.confirmPassword && (
                    <span className="form-error">
                      {validationErrors.confirmPassword}
                    </span>
                  )}
                </div>
              </div>

              {error && (
                <div className="user-list-error" style={{ margin: "1rem 0" }}>
                  {error}
                </div>
              )}
            </form>
          </div>

          <div className="slide-panel-footer">
            <button
              type="button"
              className="btn secondary"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn primary"
              onClick={handleSubmit}
              disabled={loading}
              style={{
                background: loading ? "#ccc" : "#dc3545",
                borderColor: loading ? "#ccc" : "#dc3545",
              }}
            >
              {loading
                ? isSelfReset
                  ? "Changing..."
                  : "Resetting..."
                : isSelfReset
                ? "Change Password"
                : "Reset Password"}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ResetPassword;
